import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  disableTwoFactor,
  enableTwoFactor,
  revokeTrustedDevices,
  setupTwoFactor,
} from '../api';
import Button from './ui/Button';

/**
 * `required`: the platform makes this account use 2FA (twoFactorRequired),
 * so it cannot be turned off here.
 */
function TwoFactorSettings({ enabled, required = false, onChanged }) {
  const { t } = useTranslation();
  const [mode, setMode] = useState('status');
  const [setupData, setSetupData] = useState(null);
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');

  const [isSubmitting, setIsSubmitting] =
    useState(false);

  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] =
    useState('');

  function reset() {
    setMode('status');
    setSetupData(null);
    setCode('');
    setPassword('');
    setError('');
  }

  async function startSetup() {
    try {
      setIsSubmitting(true);
      setError('');
      setSuccessMessage('');

      const data = await setupTwoFactor();

      setSetupData(data);
      setMode('setup');
    } catch (submitError) {
      console.error(
        'Failed to start two-factor setup:',
        submitError,
      );

      setError(
        submitError.message ||
          t('twoFactor.errorSetup'),
      );
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleEnable(event) {
    event.preventDefault();

    try {
      setIsSubmitting(true);
      setError('');

      await enableTwoFactor(code.trim());

      reset();
      setSuccessMessage(
        t('twoFactor.successEnable'),
      );

      onChanged(true);
    } catch (submitError) {
      console.error(
        'Failed to enable two-factor authentication:',
        submitError,
      );

      setError(
        submitError.message ||
          t('twoFactor.errorEnable'),
      );
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleRevokeDevices() {
    try {
      setIsSubmitting(true);
      setError('');
      setSuccessMessage('');

      await revokeTrustedDevices();

      setSuccessMessage(t('twoFactor.successRevokeDevices'));
    } catch (submitError) {
      console.error('Failed to revoke trusted devices:', submitError);
      setError(submitError.message || t('twoFactor.errorRevokeDevices'));
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleDisable(event) {
    event.preventDefault();

    try {
      setIsSubmitting(true);
      setError('');

      await disableTwoFactor(password);

      reset();
      setSuccessMessage(
        t('twoFactor.successDisable'),
      );

      onChanged(false);
    } catch (submitError) {
      console.error(
        'Failed to disable two-factor authentication:',
        submitError,
      );

      setError(
        submitError.message ||
          t('twoFactor.errorDisable'),
      );
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <>
      <div className="account-setting-item">
        <div>
          <strong>
            {t('twoFactor.title')}
          </strong>

          <span>
            {enabled ? t('twoFactor.enabled') : t('twoFactor.disabled')}
          </span>
        </div>

        {mode === 'status' && enabled && required && (
          <span className="account-2fa-required">
            {t('twoFactor.requiredByPlatform')}
          </span>
        )}

        {mode === 'status' && !(enabled && required) && (
          <Button
            variant="secondary"
            disabled={isSubmitting}
            onClick={() => {
              setSuccessMessage('');

              if (enabled) {
                setMode('disable');
              } else {
                startSetup();
              }
            }}
          >
            {isSubmitting
              ? t('twoFactor.loading')
              : enabled
                ? t('twoFactor.disable')
                : t('twoFactor.enable')}
          </Button>
        )}
      </div>

      {mode === 'status' && enabled && (
        <div className="account-setting-item">
          <div>
            <strong>{t('twoFactor.trustedDevicesTitle')}</strong>
            <span>{t('twoFactor.trustedDevicesDescription')}</span>
          </div>

          <Button
            variant="secondary"
            disabled={isSubmitting}
            onClick={handleRevokeDevices}
          >
            {t('twoFactor.revokeDevices')}
          </Button>
        </div>
      )}

      {mode === 'status' &&
        (error || successMessage) && (
          <div
            className={
              error
                ? 'login-message login-message-error'
                : 'login-message login-message-success'
            }
            role={error ? 'alert' : 'status'}
          >
            <i
              className={
                error
                  ? 'fa-solid fa-circle-exclamation'
                  : 'fa-solid fa-circle-check'
              }
            />

            <span>{error || successMessage}</span>
          </div>
        )}

      {mode === 'setup' && setupData && (
        <div className="account-2fa-setup">
          <p>
            {t('twoFactor.setupInstructions')}
          </p>

          <img
            className="account-2fa-qr"
            src={setupData.qrCodeDataUrl}
            alt={t('twoFactor.qrAlt')}
          />

          <p className="account-2fa-manual-entry">
            {t('twoFactor.manualEntryPrefix')}{' '}
            <code>{setupData.secret}</code>
          </p>

          <form
            className="account-form"
            onSubmit={handleEnable}
            noValidate
          >
            {error && (
              <div
                className="login-message login-message-error"
                role="alert"
              >
                <i className="fa-solid fa-circle-exclamation" />
                <span>{error}</span>
              </div>
            )}

            <div className="account-field-row">
              <label htmlFor="twoFactorCode">
                {t('twoFactor.codeLabel')}
              </label>

              <input
                id="twoFactorCode"
                name="twoFactorCode"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                placeholder="000000"
                value={code}
                disabled={isSubmitting}
                onChange={(event) => {
                  setCode(
                    event.target.value.replace(
                      /\D/g,
                      '',
                    ),
                  );

                  setError('');
                }}
              />
            </div>

            <div className="account-actions">
              <Button
                variant="secondary"
                disabled={isSubmitting}
                onClick={reset}
              >
                {t('twoFactor.cancel')}
              </Button>

              <Button
                variant="primary"
                type="submit"
                disabled={
                  isSubmitting || code.length !== 6
                }
              >
                {isSubmitting
                  ? t('twoFactor.verifying')
                  : t('twoFactor.confirm')}
              </Button>
            </div>
          </form>
        </div>
      )}

      {mode === 'disable' && (
        <form
          className="account-form"
          onSubmit={handleDisable}
          noValidate
        >
          {error && (
            <div
              className="login-message login-message-error"
              role="alert"
            >
              <i className="fa-solid fa-circle-exclamation" />
              <span>{error}</span>
            </div>
          )}

          <div className="account-field-row">
            <label htmlFor="twoFactorDisablePassword">
              {t('twoFactor.passwordLabel')}
            </label>

            <input
              id="twoFactorDisablePassword"
              name="twoFactorDisablePassword"
              type="password"
              autoComplete="current-password"
              placeholder={t('twoFactor.passwordPlaceholder')}
              value={password}
              disabled={isSubmitting}
              onChange={(event) => {
                setPassword(event.target.value);
                setError('');
              }}
            />
          </div>

          <div className="account-actions">
            <Button
              variant="secondary"
              disabled={isSubmitting}
              onClick={reset}
            >
              {t('twoFactor.cancel')}
            </Button>

            <Button
              variant="primary"
              type="submit"
              disabled={isSubmitting || !password}
            >
              {isSubmitting
                ? t('twoFactor.disabling')
                : t('twoFactor.disableButton')}
            </Button>
          </div>
        </form>
      )}
    </>
  );
}

export default TwoFactorSettings;
